import {
  registerDecorator,
  type ValidationArguments,
  type ValidationOptions,
} from 'class-validator';

/**
 * Class validator that checks if a value is less than or equal to the value of
 * another property. Pass `relatedValueNullable: true` to skip the check when the
 * related property is null/undefined (useful when the related field is optional).
 * @param property          Property name to compare to.
 * @param validationOptions
 */
export function IsLessThanOrEqualTo<T>(
  property: keyof T,
  validationOptions?: ValidationOptions & { relatedValueNullable?: boolean },
) {
  return function (object: unknown, propertyName: string) {
    registerDecorator({
      name: 'isLessThanOrEqualTo',
      target: object.constructor,
      propertyName,
      constraints: [property],
      options: validationOptions,
      validator: {
        validate(value: unknown, args: ValidationArguments) {
          const [relatedPropertyName] = args.constraints;
          const relatedValue: unknown = Reflect.get(
            args.object,
            relatedPropertyName,
          );

          if (validationOptions?.relatedValueNullable && relatedValue == null)
            return true;

          return (
            typeof value === 'number' &&
            typeof relatedValue === 'number' &&
            value <= relatedValue
          );
        },

        defaultMessage(args: ValidationArguments) {
          const [relatedPropertyName] = args.constraints;
          return `${args.property} must be less than or equal to ${relatedPropertyName}`;
        },
      },
    });
  };
}
